import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Loader2, CheckCircle, User } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    label: "",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    pincode: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    setFormData({
      email: userData.email || "",
      label: userData.label || "",
      address_line1: userData.address_line1 || "",
      address_line2: userData.address_line2 || "",
      address_line3: userData.address_line3 || "",
      pincode: userData.pincode || ""
    });
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pincode") {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!formData.pincode || formData.pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }
    
    // Check if pincode is serviceable
    if (formData.pincode !== "560075") {
      alert("Sorry, we are not serving in this area");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const response = await axios.put(`${API}/users/${user.id}`, {
        email: formData.email,
        label: formData.label,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        address_line3: formData.address_line3,
        pincode: formData.pincode
      });

      if (response.data.success) {
        const updatedUser = { ...user, ...formData };
        localStorage.setItem("fleety_user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        setIsEditing(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.data.message || "Failed to update profile");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Profile</h1>
          {!isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              className="ml-auto text-[#3FA9F5] border-[#3FA9F5]"
              data-testid="edit-btn"
            >
              Edit
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-700">Profile updated successfully!</span>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          {/* Avatar */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="w-16 h-16 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
              <User size={32} className="text-[#3FA9F5]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{user.full_name}</h2>
              <p className="text-gray-500">{user.phone_number}</p>
            </div>
          </div>

          {/* Non-editable fields */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
              <div className="h-12 px-4 bg-gray-100 rounded-xl flex items-center text-gray-600">
                {user.full_name}
                <span className="ml-auto text-xs text-gray-400">Cannot be changed</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
              <div className="h-12 px-4 bg-gray-100 rounded-xl flex items-center text-gray-600">
                {user.phone_number}
                <span className="ml-auto text-xs text-gray-400">Cannot be changed</span>
              </div>
            </div>
          </div>

          {/* Editable fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Label</label>
              <Input
                type="text"
                name="label"
                placeholder="e.g., Home, Office"
                value={formData.label}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="label-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
              <Input
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="address1-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <Input
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="address2-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 3</label>
              <Input
                type="text"
                name="address_line3"
                value={formData.address_line3}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="address3-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
              <Input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                disabled={!isEditing}
                className={`h-12 px-4 rounded-xl ${!isEditing ? 'bg-gray-50' : 'bg-white border-2 border-gray-200 focus:border-[#3FA9F5]'}`}
                data-testid="pincode-input"
              />
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm mt-4">{error}</p>
          )}

          {/* Save/Cancel Buttons */}
          {isEditing && (
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    email: user.email || "",
                    label: user.label || "",
                    address_line1: user.address_line1 || "",
                    address_line2: user.address_line2 || "",
                    address_line3: user.address_line3 || "",
                    pincode: user.pincode || ""
                  });
                  setError("");
                }}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white rounded-xl"
                data-testid="save-btn"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
